import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import { BASE_URL, security_key } from "../../../global";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import LinearProgress from "@mui/material/LinearProgress";

const AddMember = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const formRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [user, setUser] = useState(null);
    const [showPassword, setShowPassword] = useState(false);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    useEffect(() => {
        const fetchMember = async () => {
            try {
                const response = await axios.post(BASE_URL + "API/apicall", {
                    security_key: security_key,
                    pk_user_key: id,
                    mode: "getUserData",
                });
                const result = response.data;
                setUser(result);
            } catch (error) {
                toast.error("Error fetching magazine details.", { autoClose: 3000 });
            }
        };

        fetchMember();
    }, [id]);

    const reverseString = (str) => {
        return str.split("").reverse().join("");
    };

    const initialValues = {
        pk_user_key: user ? user.pk_user_key : "",
        referred_by: user ? reverseString(user.user_referred_by) : "",
        pk_ud_key: user ? user.pk_ud_key : "",
        user_name: user ? user.user_name : "",
        first_name: user ? user.first_name : "",
        last_name: user ? user.last_name : "",
        gender: user ? user.gender : "",
        dob: user ? user.dob : "",
        user_email: user ? user.user_email : "",
        password: user ? user.direct_password : "",
        user_type: "3",
        user_status: "1",
        phone: user && user.phone ? user.phone : "",
        address_line1: user ? user.address_line1 : "",
        address_line2: user ? user.address_line2 : "",
        country: user ? user.country : "",
        state: user ? user.state : "",
        city: user ? user.city : "",
        zip_code: user ? user.zip_code : "",
        updated_by: sessionStorage.getItem("pk_user_key"),
    };

    const basicSchema = Yup.object().shape({
        referred_by: Yup.string()
            .length(16, "Referral Code must be exactly 16 characters.")
            .matches(/^[a-zA-Z0-9]*$/, "Referral Code can only contain letters and numbers.")
            .required("Referral Code is required.")
            .test("unique-link", "Invalid referral code", async function (value) {
                if (!value) return true;
                return await checkReferralCode(value);
            }),
        user_name: Yup.string()
            .required("Username is required.")
            .test("no-leading-space", "No spaces allowed at the beginning.", function (value) {
                if (!value) return true;
                return !/^\s/.test(value);
            })
            .test("starts-with-letter", "Username must start with a letter.", function (value) {
                if (!value) return true; // Skip validation if value is empty
                return /^[a-zA-Z]/.test(value);
            })
            .test("valid-characters", "Only letters, numbers, and underscores allowed.", function (value) {
                if (!value) return true; // Skip validation if value is empty
                return /^[a-zA-Z0-9_]*$/.test(value);
            })
            .test("length", "Username must be 3-20 characters long.", function (value) {
                if (!value) return true; // Skip validation if value is empty
                return value.length >= 3 && value.length <= 20;
            })
            // .matches(
            //     /^[a-zA-Z][a-zA-Z0-9_]{2,19}$/,
            //     "Username must be 3-20 characters long, start with a letter, and can only contain letters, numbers, and underscores."
            // )
            .test("no-trailing-space", "No spaces allowed at the end.", function (value) {
                if (!value) return true;
                return !/\s$/.test(value);
            })
            .matches(/^(?!.*__)/, "Username cannot contain consecutive underscores.")
            .test("unique-link", "This username is already taken. Please choose another one.", async function (value) {
                if (!value) return true;
                const isUserNameUnique = await checkUserName(value);
                return isUserNameUnique;
            }),

        first_name: Yup.string()
            .required("First Name is required.")
            .test("no-leading-space", "No spaces allowed at the beginning.", function (value) {
                if (!value) return true;
                return !/^\s/.test(value);
            })
            .min(3, "First Name must be at least 3 characters.")
            .max(50, "First Name must be at most 50 characters.")
            .test("no-trailing-space", "No spaces allowed at the end.", function (value) {
                if (!value) return true;
                return !/\s$/.test(value);
            })
            .matches(/^[a-zA-Z]*$/, "First Name can only contain letters."),

        last_name: Yup.string()
            .required("Last Name is required.")
            .test("no-leading-space", "No spaces allowed at the beginning.", function (value) {
                if (!value) return true;
                return !/^\s/.test(value);
            })
            .min(1, "Last Name must be at least 1 characters.")
            .max(250, "Last Name must be at most 50 characters.")
            .test("no-trailing-space", "No spaces allowed at the end.", function (value) {
                if (!value) return true;
                return !/\s$/.test(value);
            })
            .matches(/^[a-zA-Z]*$/, "Last Name can only contain letters."),

        gender: Yup.number().oneOf([1, 2], "Gender must be either 1 (Male) or 2 (Female).").required("Gender is required."),

        dob: Yup.date()
            .required("Date of Birth is required.")
            .max(new Date(), "Date of Birth cannot be in the future.") // Ensure DOB is not in the future
            .test("age", "You must be at least 18 years old.", (value) => {
                if (!value) return false; // If no value, return false
                const today = new Date();
                const birthDate = new Date(value);
                const age = today.getFullYear() - birthDate.getFullYear();
                const monthDiff = today.getMonth() - birthDate.getMonth();
                return age > 18 || (age === 18 && monthDiff >= 0); // Check if 18 or older
            }),

        address_line1: Yup.string().max(100, "Address Line 1 must be at most 100 characters.").required("Address Line 1 is required."),

        address_line2: Yup.string().max(100, "Address Line 2 must be at most 100 characters."),

        country: Yup.string().max(50, "Country must be at most 50 characters.").required("Country is required."),

        city: Yup.string().max(50, "City must be at most 50 characters.").required("City is required."),

        state: Yup.string().max(50, "State must be at most 50 characters.").required("State is required."),

        zip_code: Yup.string()
            .matches(/^\d{5}(-\d{4})?$/, "Zipcode must be a 5-digit number or a 5+4 format (e.g., 12345 or 12345-6789).")
            .required("Zipcode is required."),

        user_email: Yup.string()
            .email("Invalid email address.")
            .required("email is required.")
            .test("unique-link", "User Email is already existing in the database", async function (value) {
                if (!value) return true;
                const isUserEmailUnique = await checkUserEmail(value);
                return isUserEmailUnique;
            }),

        password: Yup.string()
            .min(8, "Password must be at least 8 characters.")
            .max(100, "Password must be at most 100 characters.")
            .matches(/[a-zA-Z]/, "Password must contain at least one letter.")
            .matches(/\d/, "Password must contain at least one number.")
            .matches(/[@$!%*?&#]/, "Password must contain at least one special character.")
            .required("Password is required."),

        phone: Yup.string()
            .required("Phone number is required.")
            .min(11, "Phone number must be at least 10 digits.")
            .max(16, "Phone number must not exceed 15 digits.")
            .test("unique-digits", "Invalid phone number.", function (value) {
                const cleanedDigits = value.replace(/^\+\D*/, "");
                const digitsWithoutCountryCode = cleanedDigits.slice(1);
                const hasDifferentDigit = Array.from(new Set(digitsWithoutCountryCode)).length !== 1;
                return hasDifferentDigit || this.createError({ message: "Invalid phone number." });
            }),
    });

    async function checkReferralCode(referred_by) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkReferralCode",
            referral_code: referred_by,
        });

        const output = response.data;

        return output == 1 ? true : false;
    }

    async function checkUserName(user_name) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkUserName",
            pk_user_key: id,
            user_name: user_name,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }

    async function checkUserEmail(user_email) {
        const response = await axios.post(BASE_URL + "API/apicall", {
            security_key: security_key,
            mode: "checkUserEmail",
            pk_user_key: id,
            user_email: user_email,
        });

        const output = response.data;

        return output == 1 ? false : true;
    }

    const formik = useFormik({
        initialValues,
        validationSchema: basicSchema,
        enableReinitialize: true,
        onSubmit: async (formValues) => {
            toast.dismiss();

            setIsSubmitting(true);

            const response = await axios.post(
                BASE_URL + "API/apicall",
                {
                    security_key: security_key,
                    mode: "updateMemberData",
                    ...formValues,
                },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            const output = response.data;
            if (output == 1) {
                toast.success("Updated successfully!");
                setTimeout(() => navigate("/downline"), 3000);
            } else {
                toast.error("Oops! Something went wrong.", {
                    autoClose: 3000,
                });
            }
            setTimeout(function () {
                setIsSubmitting(false);
            }, 3000);
        },
    });

    const { values, handleChange, handleBlur, touched, errors, handleSubmit, setFieldValue, resetForm, setValues, setTouched } = formik;

    return (
        <section className="pb-3">
            <ToastContainer position="top-center" />
            <div className="main-content">
                <h3 className="page-title">Edit Member</h3>
                <div className="">
                    <form onSubmit={handleSubmit} ref={formRef}>
                        <div className="card mb-2">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-12 col-lg-6">
                                        <h5>General Information</h5>
                                        <div className="form-group">
                                            <label>
                                                Referred by <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="referred_by"
                                                value={values.referred_by}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                readOnly={true}
                                            />
                                            {errors.referred_by && touched.referred_by && <p className="text-danger">{errors.referred_by}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                First Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="first_name"
                                                value={values.first_name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter first name"
                                            />
                                            {errors.first_name && touched.first_name && <p className="text-danger">{errors.first_name}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Gender <span className="text-danger">*</span>
                                            </label>
                                            <select
                                                className="form-control bg-7"
                                                name="gender"
                                                value={values.gender}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            >
                                                <option value="">Select Gender</option>
                                                <option value="1">Male</option>
                                                <option value="2">Female</option>
                                            </select>
                                            {errors.gender && touched.gender && <p className="text-danger">{errors.gender}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Password <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                className="form-control bg-7"
                                                name="password"
                                                value={values.password}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                autoComplete="new-password"
                                                placeholder="Enter password"
                                            />
                                            <div className="position-absolute" style={{ bottom: "22px", right: "350px" }}>
                                                <button
                                                    type="button"
                                                    onClick={togglePasswordVisibility}
                                                    className="password-toggle"
                                                    style={{
                                                        background: "none",
                                                        border: "none",
                                                        cursor: "pointer",
                                                    }} // Optional: style for better UX
                                                    dangerouslySetInnerHTML={{
                                                        __html: showPassword ? `<i class="fa-regular fa-eye-slash"></i>` : `<i class="fa-regular fa-eye"></i>`,
                                                    }}
                                                />
                                            </div>
                                            {errors.password && touched.password && <p className="text-danger">{errors.password}</p>}
                                        </div>
                                    </div>
                                    <div className="col-md-12 col-lg-6">
                                        <h5>&nbsp;</h5>
                                        <div className="form-group">
                                            <label>
                                                User Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="user_name"
                                                value={values.user_name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter user name"
                                            />
                                            {errors.user_name && touched.user_name && <p className="text-danger">{errors.user_name}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Last Name <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="last_name"
                                                value={values.last_name}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter last name"
                                            />
                                            {errors.last_name && touched.last_name && <p className="text-danger">{errors.last_name}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                DOB <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="date"
                                                className="form-control bg-7"
                                                name="dob"
                                                value={values.dob}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                            />
                                            {errors.dob && touched.dob && <p className="text-danger">{errors.dob}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card mt-3">
                            <div className="card-body">
                                <div className="row">
                                    <h5>Contact Information</h5>
                                    <div className="col-md-12 col-lg-6">
                                        <div className="form-group">
                                            <label>
                                                Email Address <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="email"
                                                className="form-control bg-7"
                                                name="user_email"
                                                value={values.user_email}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter email"
                                            />
                                            {errors.user_email && touched.user_email && <p className="text-danger">{errors.user_email}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Phone Number <span className="text-danger">*</span>
                                            </label>
                                            {/* <input
                                            type="tel"
                                            className="form-control bg-7"
                                            name="phone"
                                            value={values.phone}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                        /> */}
                                            <PhoneInput
                                                // inputStyle={{ width: "100%" }}
                                                id="phone"
                                                name="phone"
                                                countryCodeEditable={false}
                                                country="us"
                                                value={values.phone}
                                                onlyCountries={["us"]}
                                                onChange={(value, data) => {
                                                    setFieldValue("phone", value);
                                                }}
                                                // onBlur={(value, data) => {
                                                //     setFieldValue("phone", value);
                                                // }}
                                                // onChange={(value) => {
                                                //     const event = {
                                                //         target: {
                                                //             name: "phone",
                                                //             value: value,
                                                //         },
                                                //     };
                                                //     handleChange(event);
                                                // }}
                                                onBlur={(e) => {
                                                    const event = {
                                                        target: {
                                                            name: "phone",
                                                            value: e.target.value,
                                                        },
                                                    };
                                                    handleBlur(event);
                                                    setTouched({ ...touched, phone: true });
                                                }}
                                            />
                                            {errors.phone && touched.phone && <p className="text-danger">{errors.phone}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Address Line 1 <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="address_line1"
                                                value={values.address_line1}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter address line 1"
                                            />
                                            {errors.address_line1 && touched.address_line1 && <p className="text-danger">{errors.address_line1}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Address Line 2 <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="address_line2"
                                                value={values.address_line2}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter address line 2"
                                            />
                                            {errors.address_line2 && touched.address_line2 && <p className="text-danger">{errors.address_line2}</p>}
                                        </div>
                                    </div>
                                    <div className="col-md-12 col-lg-6">
                                        <div className="form-group">
                                            <label>
                                                City <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="city"
                                                value={values.city}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter city"
                                            />
                                            {errors.city && touched.city && <p className="text-danger">{errors.city}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                State <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="state"
                                                value={values.state}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter state"
                                            />
                                            {errors.state && touched.state && <p className="text-danger">{errors.state}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Country <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="country"
                                                value={values.country}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter country"
                                            />
                                            {errors.country && touched.country && <p className="text-danger">{errors.country}</p>}
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                Zip Code <span className="text-danger">*</span>
                                            </label>
                                            <input
                                                type="text"
                                                className="form-control bg-7"
                                                name="zip_code"
                                                value={values.zip_code}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter zipcode"
                                            />
                                            {errors.zip_code && touched.zip_code && <p className="text-danger">{errors.zip_code}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="btn-user mt-2">
                            <button type="submit" className="btn text-white btn-dark rounded-3">
                                Save
                                {isSubmitting && <LinearProgress />}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
};

export default AddMember;
